import type { AfterViewInit, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  ViewChild,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import type { Route, Routes } from '@angular/router';
import { IconModule, IconSize } from '@freelancer/ui/icon';
import { InputSize } from '@freelancer/ui/input';
import type { SearchItem } from '@freelancer/ui/search';
import { SearchComponent as FlSearchComponent } from '@freelancer/ui/search';
import { componentStoryMap } from '../../generated/component-story-map';
import { componentRoutes } from '../../generated/component.routes';
import { directiveStoryMap } from '../../generated/directive-story-map';
import { directiveRoutes } from '../../generated/directive.routes';
import { patternStoryMap } from '../../generated/patterns-story-map';
import { patternRoutes } from '../../generated/patterns.routes';
import { pipeStoryMap } from '../../generated/pipe-story-map';
import { pipeRoutes } from '../../generated/pipe.routes';
import { prettyPrint } from '../../helpers';

interface StoryMap {
  [name: string]: {
    path: string;
    tags: readonly string[];
    internalOnly: boolean;
    stories: readonly string[];
  };
}

interface StoryLinkItem {
  title: string;
  link: string;
}

interface BitsSearchContext {
  name: string;
  link: string;
  searchableText: string;
  description?: string;
  stories?: readonly StoryLinkItem[];
}

@Component({
  standalone: true,
  selector: 'app-search',
  imports: [FlSearchComponent, IconModule],
  template: `
    <fl-search
      [itemTypeTitles]="{
        info: 'Info',
        foundations: 'Foundations',
        components: 'Components',
        directives: 'Directives',
        pipes: 'Pipes',
        patterns: 'Patterns',
        motion: 'Motion System',
        brand: 'Brand Style Guide',
      }"
      [itemTemplates]="{
        info: searchItemTemplateRef,
        foundations: searchItemTemplateRef,
        components: searchItemTemplateRef,
        directives: searchItemTemplateRef,
        pipes: searchItemTemplateRef,
        patterns: searchItemTemplateRef,
        motion: searchItemTemplateRef,
        brand: searchItemTemplateRef,
      }"
      [resultsBorderRadius]="'xlarge'"
      [searchResults]="searchResults"
      [displayResults]="displaySearchResults"
      [scrollableSearchResults]="overlayActive ? true : scrollableSearchResults"
      [scrollableResultsMaxHeight]="
        overlayActive ? 450 : scrollableResultsMaxHeight
      "
      [resultsInFlow]="overlayActive ? true : resultsInFlow"
      [useResultsCard]="overlayActive ? false : useResultsCard"
      [inputSearchSize]="overlayActive ? InputSize.LARGE : inputSearchSize"
      [showShortcutKey]="overlayActive ? false : showShortcutKey"
      [showEscHint]="overlayActive"
      [showKeyboardHints]="overlayActive"
      [autoHighlightFirst]="false"
      [showNestedStories]="true"
      [displayResultsWithEmptyInput]="
        overlayActive ? true : displayResultsWithEmptyInput
      "
      [showEmptyResultsOnKeyboardNavigationOnly]="
        overlayActive ? true : showEmptyResultsOnKeyboardNavigationOnly
      "
      [isExpandable]="isExpandable"
      [expandableIconLight]="expandableIconLight"
      [preventExpand]="overlayActive ? false : openInModal"
      [overlayMode]="overlayActive"
      (overlayClose)="closeOverlay()"
      (onFocus)="handleSearchFocus()"
      (query)="handleQuery($event)"
      (select)="handleSelection($event)"
    >
      <ng-template
        #searchItemTemplateRef
        let-item
        let-selected="selected"
      >
        <div
          class="SearchItem"
          [class.SearchItem-expanded]="selected"
        >
          <p class="SearchItem-header text-body-16">
            <span class="SearchItem-title">{{ item.name }}</span>
            @if (item.stories?.length > 0) {
              <fl-icon
                class="SearchItem-chevron"
                [name]="'ui-chevron-down'"
                [size]="IconSize.MID"
                [useIconFont]="true"
                [fillIconFont]="true"
              ></fl-icon>
            }
          </p>
          @if (item.description) {
            <p class="SearchItem-description text-body-16">
              {{ item.description }}
            </p>
          }
        </div>
      </ng-template>
    </fl-search>
  `,
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  RESULTS_MAX_HEIGHT = 270;
  IconSize = IconSize;
  InputSize = InputSize;
  overlayActive = false;

  @ViewChild(FlSearchComponent)
  searchComponent: FlSearchComponent;

  displaySearchResults = true;
  searchResults: readonly SearchItem[] = [];
  searchItems: readonly SearchItem[];
  private descriptionCache = new Map<string, string>();
  private pendingDescriptionLoads = new Set<string>();

  @Input() isExpandable = false;
  @Input() inputSearchSize = InputSize.MID;
  @Input() showShortcutKey = false;
  @Input() showEscHint = false;
  @Input() displayResultsWithEmptyInput = false;
  @Input() showEmptyResultsOnKeyboardNavigationOnly = false;
  @Input() scrollableSearchResults = true;
  @Input() scrollableResultsMaxHeight = this.RESULTS_MAX_HEIGHT;
  @Input() resultsInFlow = false;
  @Input() useResultsCard = true;
  @Input() expandableIconLight = false;
  @Input() hideInternalPages = true;
  @Input() enableKeyboardShortcut = false;
  @Input() openInModal = false;
  @Input() autofocus = false;

  readonly internalOnlyStaticPages = ['how-to-contribute', 'report-bugs'];
  readonly infoPages: readonly string[] = [
    'introduction',
    'getting-started',
    'faq',
    'how-to-contribute',
    'report-bugs',
  ];
  readonly foundationPages: readonly string[] = [
    'accessibility',
    'colors',
    'grid',
    'spacing',
    'typography',
    'illustrations',
    'copywriting',
    'layouts',
    'forms',
    'error-handling',
  ];
  readonly motionPages: readonly string[] = [
    'getting-started',
    'attention-seeker',
    'fade',
    'slide',
    'grow',
  ];
  readonly brandPages: readonly string[] = ['brand-style-guide'];

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    if (!this.enableKeyboardShortcut) {
      return;
    }

    if (
      (event.metaKey || event.ctrlKey) &&
      !event.shiftKey &&
      !event.altKey &&
      event.key.toLowerCase() === 'k'
    ) {
      const target = event.target as HTMLElement | undefined;
      const isTypingTarget =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable;

      if (isTypingTarget) {
        return;
      }

      event.preventDefault();
      if (this.openInModal) {
        this.openOverlay();
        return;
      }

      this.focusSearchInput();
    }
  }

  focusSearchInput(): void {
    this.searchComponent?.inputComponent?.nativeElement?.nativeElement?.focus();
  }

  ngAfterViewInit(): void {
    if (!this.autofocus) {
      return;
    }

    queueMicrotask(() => this.focusSearchInput());
  }

  ngOnInit(): void {
    this.searchItems = [
      ...this.generateStaticSearchItems(
        'info',
        this.getStaticPageList(this.infoPages),
      ),
      ...this.generateStaticSearchItems(
        'foundations',
        this.getStaticPageList(this.foundationPages),
      ),
      ...this.generateStorySearchItems('components', componentStoryMap),
      ...this.generateStorySearchItems('directives', directiveStoryMap),
      ...this.generateStorySearchItems('pipes', pipeStoryMap),
      ...this.generateStorySearchItems('patterns', patternStoryMap),
      ...this.generateStaticSearchItems(
        'motion',
        this.getStaticPageList(this.motionPages),
      ),
      ...this.generateStaticSearchItems(
        'brand',
        this.getStaticPageList(this.brandPages),
      ),
    ];
    this.searchResults = this.searchItems;
    this.loadDescriptionsForResults(this.searchResults);
  }

  handleSearchFocus(): void {
    if (!this.openInModal) {
      return;
    }

    this.openOverlay();
  }

  private openOverlay(): void {
    this.overlayActive = true;
    this.cdr.markForCheck();
  }

  closeOverlay(): void {
    this.overlayActive = false;
    this.cdr.markForCheck();
  }

  getStaticPageList(items: readonly string[]): readonly string[] {
    if (!this.hideInternalPages) {
      return items;
    }

    return items.filter(item => !this.internalOnlyStaticPages.includes(item));
  }

  generateStaticSearchItems(
    type: string,
    pages: readonly string[],
  ): readonly SearchItem[] {
    return pages
      .map(item => {
        const name = prettyPrint(item);
        const context: BitsSearchContext = {
          name,
          link: `/${type}/${item}`,
          searchableText: name.toLowerCase(),
        };

        return {
          type,
          context,
          displayValue: name,
        };
      })
      .sort((a, b) => {
        const aName = String(a.context?.name ?? '');
        const bName = String(b.context?.name ?? '');
        return aName < bName ? -1 : 1;
      });
  }

  generateStorySearchItems(
    type: string,
    storyMap: StoryMap,
  ): readonly SearchItem[] {
    return Object.entries(storyMap)
      .filter(([_, value]) => !this.hideInternalPages || !value.internalOnly)
      .map(([componentName, value]) => {
        const name = prettyPrint(componentName);
        const variantStories = value.stories.map(story => ({
          title: prettyPrint(story),
          link: `/${type}/${componentName}/${story}`,
        }));
        const stories = [
          {
            title: 'Default',
            link: `/${type}/${componentName}`,
          },
          ...variantStories.filter(
            story => story.title.toLowerCase() !== 'default',
          ),
        ];
        const searchableText = [
          name,
          ...stories.map(story => `${name} ${story.title}`),
        ]
          .join(' ')
          .toLowerCase();

        return {
          type,
          context: {
            name,
            link: `/${type}/${componentName}`,
            searchableText,
            stories,
          },
          displayValue: name,
        };
      })
      .sort((a, b) => {
        const aName = String(a.context?.name ?? '');
        const bName = String(b.context?.name ?? '');
        return aName < bName ? -1 : 1;
      });
  }

  private levenshteinDistance(a: string, b: string): number {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;

    const matrix: number[][] = new Array(b.length + 1)
      .fill(0)
      .map(() => new Array(a.length + 1).fill(0));

    for (let i = 0; i <= a.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= b.length; j++) matrix[j][0] = j;

    for (let j = 1; j <= b.length; j++) {
      for (let i = 1; i <= a.length; i++) {
        const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1, // deletion
          matrix[j - 1][i] + 1, // insertion
          matrix[j - 1][i - 1] + substitutionCost, // substitution
        );
      }
    }

    return matrix[b.length][a.length];
  }

  handleQuery(query: string): void {
    this.displaySearchResults = true;
    const queryLower = query.toLowerCase();
    const normalizedQuery = queryLower.replace(/\s+/g, '/');
    const filteredResults = this.searchItems.filter(item => {
      const searchableText = String(
        item.context?.searchableText ?? '',
      ).toLowerCase();
      return (
        searchableText.includes(queryLower) ||
        searchableText.includes(normalizedQuery)
      );
    });
    this.searchResults = filteredResults.sort((a, b) => {
      const aName = String(a.context?.name ?? '').toLowerCase();
      const bName = String(b.context?.name ?? '').toLowerCase();
      const aExactMatch = aName === queryLower || aName === normalizedQuery;
      const bExactMatch = bName === queryLower || bName === normalizedQuery;

      if (aExactMatch && !bExactMatch) return -1;
      if (!aExactMatch && bExactMatch) return 1;

      const aDistance = this.levenshteinDistance(aName, queryLower);
      const bDistance = this.levenshteinDistance(bName, queryLower);
      return aDistance - bDistance;
    });
    this.loadDescriptionsForResults(this.searchResults);
  }

  handleSelection(selection: SearchItem): void {
    if (selection.type === 'select') {
      return;
    }

    this.displaySearchResults = false;
    const { context } = selection;
    const link = String(context?.link ?? '');
    this.closeOverlay();
    this.router.navigate([link]);
  }

  private loadDescriptionsForResults(results: readonly SearchItem[]): void {
    results.forEach(item => this.loadDescriptionForItem(item));
  }

  private loadDescriptionForItem(item: SearchItem): void {
    const { context } = item;
    if (context.description || !context.link || !context.stories?.length) {
      return;
    }

    const componentName = context.link.split('/')[2];
    if (!componentName) {
      return;
    }

    const cacheKey = `${item.type}:${componentName}`;
    const cachedDescription = this.descriptionCache.get(cacheKey);
    if (cachedDescription !== undefined) {
      context.description = cachedDescription;
      return;
    }

    if (this.pendingDescriptionLoads.has(cacheKey)) {
      return;
    }

    const storyRoute = this.getStoryRoute(item.type, componentName);
    if (!storyRoute?.loadChildren) {
      return;
    }

    this.pendingDescriptionLoads.add(cacheKey);
    Promise.resolve(storyRoute.loadChildren())
      .then(loadedRoutes$ => {
        const routes = loadedRoutes$ as Routes;
        const storiesComponentRoute = routes[0];
        const storiesComponentCtor = storiesComponentRoute?.component as
          | (new () => { readme?: string })
          | undefined;
        const readme = storiesComponentCtor
          ? ((
              Reflect.construct(storiesComponentCtor, []) as { readme?: string }
            ).readme ?? '')
          : '';
        const description = this.extractDescription(readme);
        this.descriptionCache.set(cacheKey, description);
        context.description = description;
        this.cdr.markForCheck();
      })
      .catch(() => {
        this.descriptionCache.set(cacheKey, '');
      })
      .finally(() => {
        this.pendingDescriptionLoads.delete(cacheKey);
      });
  }

  private getStoryRoute(
    type: string,
    componentName: string,
  ): Route | undefined {
    const routeMap: Record<string, readonly Route[]> = {
      components: componentRoutes,
      directives: directiveRoutes,
      patterns: patternRoutes,
      pipes: pipeRoutes,
    };

    return routeMap[type]?.find(route => route.path === componentName);
  }

  private extractDescription(readme: string): string {
    const firstLine = (readme.split('\n')[0] || '').trim();
    if (!firstLine.startsWith('#')) {
      return '';
    }

    return firstLine.substring(1).trim();
  }
}
